import { authService } from './authService';

// AWS API Gateway URL - set in .env as VITE_AWS_API_GATEWAY_URL
const API_GATEWAY_URL = import.meta.env.VITE_AWS_API_GATEWAY_URL || '';

export interface UserProfile {
  UserStateID: number;
  email: string;
  playerFirstName: string;
  playerLastName: string;
  provider: string;
  profileImageUrl: string | null;
  profileImageKey: string | null;
  didUserPay: boolean;
  createdAt: number;
  lastProfileUpdate: number;
  lastPaymentUpdate?: number;
}

export interface UpdateProfileData {
  playerFirstName?: string;
  playerLastName?: string;
}

export interface ImageUploadResponse {
  uploadUrl: string;
  imageUrl: string;
  key: string;
}

export const accountService = {
  /**
   * Get user profile from DynamoDB
   */
  getProfile: async (): Promise<{ success: boolean; data?: UserProfile; error?: string }> => {
    try {
      const token = await authService.getJwtToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_GATEWAY_URL}/auth/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get profile' }));
        return { success: false, error: errorData.error || 'Failed to get profile' };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error: any) {
      console.error('Error getting profile:', error);
      return { success: false, error: error.message || 'Failed to get profile' };
    }
  },

  /**
   * Update user profile in DynamoDB
   */
  updateProfile: async (data: UpdateProfileData): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await authService.getJwtToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_GATEWAY_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update profile' }));
        return { success: false, error: errorData.error || 'Failed to update profile' };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message || 'Failed to update profile' };
    }
  },

  /**
   * Get presigned S3 URL for uploading profile image
   */
  getImageUploadUrl: async (): Promise<{ success: boolean; data?: ImageUploadResponse; error?: string }> => {
    try {
      const token = await authService.getJwtToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_GATEWAY_URL}/auth/profile/image/upload-url`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get upload URL' }));
        return { success: false, error: errorData.error || 'Failed to get upload URL' };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error: any) {
      console.error('Error getting upload URL:', error);
      return { success: false, error: error.message || 'Failed to get upload URL' };
    }
  },

  /**
   * Upload image file to S3 using presigned URL
   */
  uploadImageToS3: async (file: File, uploadUrl: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'image/jpeg'
        },
        body: file
      });

      if (!response.ok) {
        return { success: false, error: 'Failed to upload image to S3' };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error uploading to S3:', error);
      return { success: false, error: error.message || 'Failed to upload image' };
    }
  },

  /**
   * Delete profile image from S3 and update user record
   */
  deleteImage: async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await authService.getJwtToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_GATEWAY_URL}/auth/profile/image`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete image' }));
        return { success: false, error: errorData.error || 'Failed to delete image' };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error deleting image:', error);
      return { success: false, error: error.message || 'Failed to delete image' };
    }
  },

  /**
   * Update user payment status
   * ADMIN ONLY - Regular users cannot call this
   * This should only be used by admin dashboards
   */
  updatePaymentStatus: async (targetUserId: string, didUserPay: boolean): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await authService.getJwtToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_GATEWAY_URL}/auth/payment-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ targetUserId, didUserPay })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update payment status' }));
        return { success: false, error: errorData.error || 'Failed to update payment status' };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error updating payment status:', error);
      return { success: false, error: error.message || 'Failed to update payment status' };
    }
  },

  /**
   * Check if user has paid (convenience method)
   */
  checkPaymentStatus: async (): Promise<{ success: boolean; didUserPay?: boolean; error?: string }> => {
    try {
      const result = await accountService.getProfile();

      if (!result.success || !result.data) {
        return { success: false, error: result.error || 'Failed to get profile' };
      }

      return {
        success: true,
        didUserPay: result.data.didUserPay
      };
    } catch (error: any) {
      console.error('Error checking payment status:', error);
      return { success: false, error: error.message || 'Failed to check payment status' };
    }
  }
};
